import React from "react";
import { useTranslation } from "react-i18next";
import { RotateCcw, Plus, LayoutList, LayoutGrid, Activity, TrendingUp, RefreshCw, Save, Download, ArrowDown, ArrowUp, ArrowUpDown, Edit2, Trash2, Shield } from "lucide-react";
import { motion } from "motion/react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { MobileRowComponent, DesktopRowComponent, PerfRowComponent, PortfolioPerfRowComponent } from "../../../components/RowComponents";
import { SortableCategoryHeader } from "../../../components/SortableCategoryHeader";
import { ThemeInput } from "../../../components/ThemeInput";
import { formatCurrency, formatPerf } from "../../../lib/utils";
import { getPerfValue, PERF_OPTIONS } from "../../../components/RowComponents";
import { TooltipSpan } from "../../../components/TooltipSpan";

import { AddTickerModal } from "./AddTickerModal";

export function PortfolioPanel({ portfolioType, isChartMaximized, selectedTicker, viewMode, sortColumn, sortDirection, setSortColumn, setSortDirection, setViewMode, showTechFundDetails, setShowTechFundDetails, performBulkAnalysis, portfolio, loading, bulkLoading, bulkProgress, handleSave, isDirty, handleExportCSV, isExample, sensors, handleDragStart, handleDragEnd, handleDragCancel, activeDragId, sortedPortfolio, liveQuotes, handleAnalyzeSingle, topPickInfo, handleSort, orderedThemes, groupedPortfolio, handleMoveThemeMobileUp, handleMoveThemeMobileDown, handleUpdateTheme, hasDecimals, handleDeleteCategory, handleUpdateTicker, handleUpdateItem, handleRemoveItem, handleMoveMobileUp, handleMoveMobileDown, listPerfMetric, setListPerfMetric, handleAddCategory, handleAddRow, totalAllocation, TickerEditor, maxRiskPerTradePct = 0.01, maxAllocationCapPct = 0.15, tradeDuration = 'position', handleUpdatePortfolioRiskSettings }: any) {
  const { t } = useTranslation();
  const [isAddTickerModalOpen, setIsAddTickerModalOpen] = React.useState(false);

  return (
    <>
      {/* Portfolio Table Area */}
          <section
            className={isChartMaximized ? "hidden" : `lg:w-7/12 flex-col space-y-4 ${selectedTicker ? "hidden lg:flex" : "flex"}`}
          >
            <div className="flex flex-row items-center justify-between border-l-2 border-emerald-500 pl-2.5 sm:pl-3 py-1 gap-2 sm:gap-4">
              <h2 className="text-[10px] lg:text-sm font-bold lg:font-black uppercase lg:tracking-[0.2em] text-zinc-400 lg:text-zinc-100 mr-1 lg:mr-4 shrink-0 transition-all">
                {t("portfolio.title")}
              </h2>
              <div className="flex flex-nowrap sm:flex-wrap items-center justify-end gap-1.5 sm:gap-2 flex-1 w-auto sm:w-full">
                {viewMode === "LIST" && sortColumn !== "default" && (
                  <button
                    onClick={() => setSortColumn("default")}
                    aria-label={t("portfolio.reset_sort")}
                    className="flex items-center gap-1.5 px-2 py-1.5 bg-zinc-800/30 hover:bg-zinc-800 text-emerald-400 hover:text-emerald-300 border border-emerald-500/20 rounded-sm text-[9px] uppercase font-bold tracking-widest transition-colors"
                    title={t("portfolio.reset_sort")}
                  >
                    <RotateCcw size={10} />
                    <span className="hidden lg:inline">
                      {t("portfolio.reset_sort")}
                    </span>
                  </button>
                )}

                {viewMode === "THEME" && (
                  <button
                    onClick={handleAddCategory}
                    aria-label={t("portfolio.new_category")}
                    className="flex shrink-0 items-center gap-1.5 px-2 py-1.5 bg-zinc-800/30 hover:bg-zinc-800 text-emerald-400 hover:text-emerald-300 border border-emerald-500/20 rounded-sm text-[9px] uppercase font-bold tracking-widest transition-colors"
                    title={t("portfolio.new_category")}
                  >
                    <Plus size={10} className="shrink-0" />
                    <span className="hidden lg:inline whitespace-nowrap">
                      {t("portfolio.new_category")}
                    </span>
                  </button>
                )}

                {viewMode === "LIST" && (
                  <button
                    onClick={() => setIsAddTickerModalOpen(true)}
                    aria-label={t("portfolio.add_ticker")}
                    className="flex shrink-0 items-center gap-1.5 px-2 py-1.5 bg-zinc-800/30 hover:bg-zinc-800 text-emerald-400 hover:text-emerald-300 border border-emerald-500/20 rounded-sm text-[9px] uppercase font-bold tracking-widest transition-colors"
                    title={t("portfolio.add_ticker")}
                  >
                    <Plus size={10} className="shrink-0" />
                    <span className="hidden lg:inline whitespace-nowrap">
                      {t("portfolio.add_ticker")}
                    </span>
                  </button>
                )}

                <div className="flex shrink-0 items-center border border-zinc-800 rounded-sm overflow-hidden text-[9px] uppercase font-bold tracking-widest">
                  <button
                    onClick={() => setViewMode("LIST")}
                    aria-label={t("portfolio.view_list")}
                    className={`px-3 py-1.5 flex shrink-0 items-center gap-1.5 transition-colors ${viewMode === "LIST" ? "bg-zinc-800 text-zinc-200" : "bg-transparent text-zinc-300 hover:text-zinc-300"}`}
                    title={t("portfolio.view_list")}
                  >
                    <LayoutList size={12} className="shrink-0" />{" "}
                    <span className="hidden lg:inline whitespace-nowrap">
                      {t("portfolio.view_list")}
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      setViewMode("THEME");
                      setSortColumn("default");
                    }}
                    aria-label={t("portfolio.view_theme")}
                    className={`px-3 py-1.5 flex shrink-0 items-center gap-1.5 transition-colors ${viewMode === "THEME" ? "bg-zinc-800 text-zinc-200" : "bg-transparent text-zinc-300 hover:text-zinc-300"}`}
                    title={t("portfolio.view_theme")}
                  >
                    <LayoutGrid size={12} className="shrink-0" />{" "}
                    <span className="hidden lg:inline whitespace-nowrap">
                      {t("portfolio.view_theme")}
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      setViewMode("PERF");
                      setSortColumn("default");
                    }}
                    aria-label="Performance"
                    className={`px-3 py-1.5 flex shrink-0 items-center gap-1.5 transition-colors ${viewMode === "PERF" ? "bg-zinc-800 text-zinc-200" : "bg-transparent text-zinc-300 hover:text-zinc-300"}`}
                    title="Performance"
                  >
                    <span className="font-mono text-xs leading-none shrink-0 border border-current rounded-full w-3 h-3 flex items-center justify-center -ml-0.5">
                      $
                    </span>{" "}
                    <span className="hidden lg:inline whitespace-nowrap">
                      Perf
                    </span>
                  </button>
                </div>

                {viewMode !== "PERF" && (
                  <div className="hidden md:flex shrink-0 items-center border border-zinc-800 rounded-sm overflow-hidden text-[9px] uppercase font-bold tracking-widest">
                    <button
                      onClick={() => setShowTechFundDetails(false)}
                      aria-label={t("analysis.overall_recommendation")}
                      className={`px-3 py-1.5 flex shrink-0 items-center gap-1.5 transition-colors ${!showTechFundDetails ? "bg-zinc-800 text-zinc-200" : "bg-transparent text-zinc-300 hover:text-zinc-300"}`}
                      title="Show Rating"
                    >
                      <Activity size={12} className="shrink-0 text-emerald-500" />
                      <span className="hidden lg:inline whitespace-nowrap">
                        {t("analysis.overall_recommendation")}
                      </span>
                    </button>
                    <button
                      onClick={() => setShowTechFundDetails(true)}
                      aria-label={t("tabs.analysis")}
                      className={`px-3 py-1.5 flex shrink-0 items-center gap-1.5 transition-colors ${showTechFundDetails ? "bg-zinc-800 text-zinc-200" : "bg-transparent text-zinc-300 hover:text-zinc-300"}`}
                      title={t("portfolio.show_details")}
                    >
                      <TrendingUp size={12} className="shrink-0 text-amber-500" />
                      <span className="hidden lg:inline whitespace-nowrap">
                        {t("tabs.analysis")}
                      </span>
                    </button>
                  </div>
                )}

                <button
                  onClick={() => {
                    const orderedItems = viewMode === "CATEGORY"
                      ? [
                          ...orderedThemes.flatMap((theme: string) => groupedPortfolio[theme]?.items || []),
                          ...(groupedPortfolio["OTHER"]?.items || [])
                        ]
                      : sortedPortfolio;
                    performBulkAnalysis(orderedItems, true);
                  }}
                  disabled={loading || bulkLoading}
                  aria-label={t("header.live_analytics")}
                  className="flex items-center gap-2 text-[10px] uppercase font-bold tracking-widest text-zinc-300 hover:text-emerald-400 disabled:opacity-50 transition-colors group"
                  title={`${t("header.live_analytics")} (${t("portfolio.tickers_cached", { count: portfolio.filter((i) => i.analysis).length })})`}
                >
                  <RefreshCw
                    size={14}
                    className={
                      loading || bulkLoading
                        ? "animate-spin text-emerald-500"
                        : "group-hover:rotate-180 transition-transform duration-500"
                    }
                  />
                  <span className="hidden sm:inline">
                    {bulkLoading && bulkProgress?.total > 0
                      ? t("header.live_analytics") + ` (${bulkProgress.loaded}/${bulkProgress.total})`
                      : t("header.live_analytics")}
                  </span>
                </button>

                <button
                  onClick={handleSave}
                  disabled={portfolio.length === 0 || loading || !isDirty}
                  aria-label={t("common.save")}
                  className="flex items-center gap-2 text-[10px] uppercase font-bold tracking-widest text-zinc-300 hover:text-emerald-400 disabled:opacity-50 disabled:hover:text-zinc-300 transition-colors"
                  title={t("portfolio.save_to_db")}
                >
                  <Save size={14} />
                  <span className="hidden sm:inline">{t("common.save")}</span>
                </button>
                <button
                  onClick={handleExportCSV}
                  disabled={portfolio.length === 0}
                  aria-label={t("common.export")}
                  className="flex items-center gap-2 text-[10px] uppercase font-bold tracking-widest text-zinc-300 hover:text-emerald-400 disabled:opacity-50 disabled:hover:text-zinc-300 transition-colors"
                  title={t("portfolio.export_to_csv")}
                >
                  <Download size={14} />
                  <span className="hidden sm:inline">{t("common.export")}</span>
                </button>
              </div>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <div
                className={`bg-zinc-900/30 rounded-sm border border-zinc-800 overflow-visible backdrop-blur-sm shadow-xl flex flex-col min-h-[400px] ${activeDragId ? "[&_input]:pointer-events-none select-none" : ""}`}
              >
                {isExample && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="bg-emerald-950/20 border-b border-emerald-500/30 text-emerald-400 p-4 flex items-start gap-3 text-sm min-h-[92px] sm:min-h-[72px]"
                  >
                    <div className="flex flex-col gap-1">
                      <span className="font-bold tracking-wide uppercase text-[10px]">
                        {t("portfolio.example_loaded_title")}
                      </span>
                      <span className="opacity-80 leading-relaxed text-[13px]">
                        {t("portfolio.example_loaded_desc")}
                      </span>
                    </div>
                  </motion.div>
                )}
                {/* Mobile Sorting Controls */}
                <div className="md:hidden flex items-center justify-between p-3 border-b border-zinc-800/60 bg-zinc-900">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-zinc-300 uppercase tracking-widest">
                      {t("portfolio.sort_label")}
                    </span>
                    {(viewMode === "LIST" || viewMode === "PERF" || viewMode === "THEME") &&
                      sortColumn !== "default" && (
                        <button
                          onClick={() => setSortColumn("default")}
                          className="p-1 text-emerald-400 hover:text-emerald-300 bg-zinc-800/30 hover:bg-zinc-800 border border-emerald-500/20 rounded-sm transition-colors"
                        >
                          <RotateCcw size={10} />
                        </button>
                      )}
                  </div>
                  <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
                    <button
                      onClick={() => handleSort("ticker")}
                      className={`px-2 py-1 flex-shrink-0 text-[10px] uppercase font-bold tracking-widest rounded-sm border transition-colors flex items-center gap-1 ${sortColumn === "ticker" ? "bg-zinc-800 text-emerald-400 border-zinc-700" : "bg-transparent text-zinc-200 border-transparent hover:bg-zinc-800/50"}`}
                    >
                      {t("common.ticker")}{" "}
                      {sortColumn === "ticker" &&
                        (sortDirection === "desc" ? "Z-A" : "A-Z")}
                    </button>
                    {viewMode === "PERF" ? (
                      [
                        "perf1D",
                        "perf1W",
                        "perf1M",
                        "perf3M",
                        "perf6M",
                        "ytdReturn",
                        "perf1Y",
                      ].map((col) => (
                        <button
                          key={col}
                          onClick={() => handleSort(col as any)}
                          className={`px-2 py-1 flex-shrink-0 text-[10px] uppercase font-bold tracking-widest rounded-sm border transition-colors flex items-center gap-1 ${sortColumn === col ? "bg-zinc-800 text-emerald-400 border-zinc-700" : "bg-transparent text-zinc-200 border-transparent hover:bg-zinc-800/50"}`}
                        >
                          {col === "ytdReturn"
                            ? "YTD(%)"
                            : `${col.replace("perf", "")}(%)`}{" "}
                          {sortColumn === col &&
                            (sortDirection === "desc" ? "↓" : "↑")}
                        </button>
                      ))
                    ) : (
                      <>
                        <button
                          onClick={() => handleSort("overall")}
                          className={`px-2 py-1 flex-shrink-0 text-[10px] uppercase font-bold tracking-widest rounded-sm border transition-colors flex items-center gap-1 ${sortColumn === "overall" ? "bg-zinc-800 text-emerald-400 border-zinc-700" : "bg-transparent text-zinc-200 border-transparent hover:bg-zinc-800/50"}`}
                        >
                          {t("analysis.overall_recommendation")}{" "}
                          {sortColumn === "overall" &&
                            (sortDirection === "desc" ? "↓" : "↑")}
                        </button>
                        {portfolioType !== "WATCHLIST" && (
                          <button
                            onClick={() => handleSort("allocation")}
                            className={`px-2 py-1 flex-shrink-0 text-[10px] uppercase font-bold tracking-widest rounded-sm border transition-colors flex items-center gap-1 ${sortColumn === "allocation" ? "bg-zinc-800 text-emerald-400 border-zinc-700" : "bg-transparent text-zinc-200 border-transparent hover:bg-zinc-800/50"}`}
                          >
                            {t("common.alloc")}{" "}
                            {sortColumn === "allocation" &&
                              (sortDirection === "desc" ? "↓" : "↑")}
                          </button>
                        )}

                      </>
                    )}
                  </div>
                </div>

                {/* Mobile Card Layout */}
                <div className="flex flex-col md:hidden divide-y divide-zinc-800/40">
                  {viewMode === "PERF" ? (
                    <>
                      {sortedPortfolio.map((item, idx) => (
                        <PerfRowComponent
                          key={`perf-mob-${item.id}`}
                          item={item}
                          isTopPick={topPickInfo?.ticker === item.ticker}
                          liveQuotes={liveQuotes}
                          onSetSelectedTicker={(t: string) =>
                            handleAnalyzeSingle(t, false, true)
                          }
                        />
                      ))}
                      {portfolio.length > 0 && !loading && (
                        <PortfolioPerfRowComponent
                          portfolio={portfolio}
                          liveQuotes={liveQuotes}
                          portfolioType={portfolioType}
                        />
                      )}
                    </>
                  ) : viewMode === "LIST" ? (
                    <SortableContext
                      items={sortedPortfolio.map((i: any) => `mob-${i.id}`)}
                      strategy={verticalListSortingStrategy}
                    >
                      {sortedPortfolio.map((item: any, idx: number) => (
                        <MobileRowComponent
                          key={`list-mob-${item.id}`}
                          sortableId={`mob-${item.id}`}
                          item={item}
                          isSelected={selectedTicker === item.ticker}
                          isTopPick={topPickInfo?.ticker === item.ticker}
                          onUpdateTicker={handleUpdateTicker}
                          onUpdateItem={handleUpdateItem}
                          onSetSelectedTicker={(t: string) =>
                            handleAnalyzeSingle(t, false, true)
                          }
                          onRemoveItem={handleRemoveItem}
                          TickerEditorComponent={TickerEditor}
                          onMoveUp={handleMoveMobileUp}
                          onMoveDown={handleMoveMobileDown}
                          isFirst={idx === 0}
                          isLast={idx === sortedPortfolio.length - 1}
                          liveQuotes={liveQuotes}
                          listPerfMetric={listPerfMetric}
                          setListPerfMetric={setListPerfMetric}
                          showTechFundDetails={false}
                          portfolioType={portfolioType}
                          tradeDuration={tradeDuration}
                        />
                      ))}
                    </SortableContext>
                  ) : (
                    <SortableContext
                      items={orderedThemes.map((t) => `category-${t}`)}
                      strategy={verticalListSortingStrategy}
                    >
                      {(() => {
                        const flatMobThemeList = orderedThemes.flatMap(
                          (t) => groupedPortfolio[t] || [],
                        );
                        return (
                          <SortableContext
                            items={flatMobThemeList.map((i) => `mob-${i.id}`)}
                            strategy={verticalListSortingStrategy}
                          >
                            {orderedThemes.map((themeName, idx) => {
                              const items = groupedPortfolio[themeName] || [];
                              const themeObjArr = items.map(
                                (p) =>
                                  parseFloat(
                                    p.allocation.replace(/[^0-9.-]+/g, ""),
                                  ) || 0,
                              );
                              const themeSubtotal = themeObjArr.reduce(
                                (a, b) => a + b,
                                0,
                              );
                              return (
                                <div
                                  key={`theme-mob-${themeName}`}
                                  className="flex flex-col border-b border-zinc-700/50"
                                >
                                  <SortableCategoryHeader
                                    id={`category-${themeName}`}
                                    className="p-3 bg-zinc-800/80"
                                    onMoveUp={() =>
                                      handleMoveThemeMobileUp(themeName)
                                    }
                                    onMoveDown={() =>
                                      handleMoveThemeMobileDown(themeName)
                                    }
                                    isFirst={idx === 0}
                                    isLast={idx === orderedThemes.length - 1}
                                  >
                                    <ThemeInput
                                      themeName={themeName}
                                      onChange={handleUpdateTheme}
                                      className="bg-transparent text-emerald-400 font-bold uppercase tracking-widest text-[11px] outline-none border-b border-transparent focus:border-zinc-500 flex-1 min-w-0 w-full"
                                    />
                                    <div className="flex items-center gap-2 shrink-0">
                                      {portfolioType !== "WATCHLIST" && (
                                        <span className="text-xs font-mono font-bold text-emerald-400 bg-emerald-950/40 border border-emerald-500/30 px-2.5 py-0.5 rounded-md shadow-sm">
                                          {themeSubtotal.toFixed(hasDecimals ? 2 : 0)}%
                                        </span>
                                      )}
                                      {items.length === 0 && (
                                        <button
                                          onClick={() =>
                                            handleDeleteCategory(themeName)
                                          }
                                          className="text-zinc-300 hover:text-red-400 transition-colors"
                                          title={
                                            t("common.delete_category") || "Delete"
                                          }
                                        >
                                          <Trash2 size={12} />
                                        </button>
                                      )}
                                    </div>
                                  </SortableCategoryHeader>
                                  {items.map((item, idx) => (
                                    <MobileRowComponent
                                      key={`theme-mob-item-${item.id}`}
                                      sortableId={`mob-${item.id}`}
                                      item={item}
                                      isSelected={selectedTicker === item.ticker}
                                      isTopPick={
                                        topPickInfo?.ticker === item.ticker
                                      }
                                      onUpdateTicker={handleUpdateTicker}
                                      onUpdateItem={handleUpdateItem}
                                      onSetSelectedTicker={(t: string) =>
                                        handleAnalyzeSingle(t, false, true)
                                      }
                                      onRemoveItem={handleRemoveItem}
                                      TickerEditorComponent={TickerEditor}
                                      onMoveUp={handleMoveMobileUp}
                                      onMoveDown={handleMoveMobileDown}
                                      isFirst={
                                        item.id === flatMobThemeList[0]?.id
                                      }
                                      isLast={
                                        item.id ===
                                        flatMobThemeList[
                                          flatMobThemeList.length - 1
                                        ]?.id
                                      }
                                      liveQuotes={liveQuotes}
                                      listPerfMetric={listPerfMetric}
                                      setListPerfMetric={setListPerfMetric}
                                      showTechFundDetails={false}
                                      portfolioType={portfolioType}
                                      tradeDuration={tradeDuration}
                                    />
                                  ))}
                                </div>
                              );
                            })}
                          </SortableContext>
                        );
                      })()}
                    </SortableContext>
                  )}
                  {portfolio.length === 0 && !loading && (
                    <div className="p-10 text-center text-zinc-300 text-sm">
                      {t("portfolio.empty_state")}
                    </div>
                  )}
                  {portfolio.length > 0 && !loading && (
                    <>
                      <div className="px-4 py-3 border-t border-zinc-800/50">
                        <button
                          onClick={() => setIsAddTickerModalOpen(true)}
                          className="w-full py-2.5 border border-dashed border-zinc-700/50 hover:border-zinc-500 rounded-sm text-[10px] uppercase font-bold tracking-widest text-zinc-300 hover:text-emerald-400 hover:bg-zinc-800/30 transition-all flex items-center justify-center gap-2"
                        >
                          <Plus size={12} /> {t("portfolio.add_ticker")}
                        </button>
                      </div>
                      {portfolioType !== "WATCHLIST" && (
                        <div className="bg-zinc-900/50 border-t border-zinc-800 flex justify-between items-center p-4">
                          <span className="text-[10px] font-bold text-zinc-300 uppercase tracking-widest">
                            {t("portfolio.total_allocation")}
                          </span>
                          <span className="text-xs sm:text-sm font-mono font-bold text-emerald-400">
                            {totalAllocation.toFixed(hasDecimals ? 2 : 0)}%
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Desktop Table Layout */}
                <div className="hidden md:block w-full">
                  {viewMode === "PERF" ? (
                    <div className="flex flex-col border-t border-zinc-800">
                      <div className="flex py-2 bg-zinc-900/80 border-b border-zinc-800 text-[10px] font-black text-zinc-400 uppercase tracking-widest items-center">
                        <span
                          className="w-32 px-4 flex-shrink-0 cursor-pointer hover:text-zinc-300 flex items-center gap-1 select-none"
                          onClick={() => handleSort("ticker")}
                        >
                          {t("common.ticker")}{" "}
                          {sortColumn === "ticker" &&
                            (sortDirection === "desc" ? "Z-A" : "A-Z")}
                        </span>
                        <div className="flex-1 grid grid-cols-7 gap-0 pr-4">
                          {[
                            "perf1D",
                            "perf1W",
                            "perf1M",
                            "perf3M",
                            "perf6M",
                            "ytdReturn",
                            "perf1Y",
                          ].map((col) => (
                            <span
                              key={col}
                              className="col-span-1 text-right cursor-pointer hover:text-zinc-300 transition-colors flex items-center justify-end gap-1 select-none"
                              onClick={() => handleSort(col as any)}
                            >
                              {col === "ytdReturn"
                                ? "YTD (%)"
                                : `${col.replace("perf", "")} (%)`}
                              {sortColumn === col &&
                                (sortDirection === "desc" ? (
                                  <ArrowDown className="w-3 h-3 text-emerald-500" />
                                ) : (
                                  <ArrowUp className="w-3 h-3 text-emerald-500" />
                                ))}
                            </span>
                          ))}
                        </div>
                      </div>
                      {sortedPortfolio.length === 0 && !loading && (
                        <div className="px-6 py-16 text-center text-zinc-300 text-sm">
                          {t("portfolio.empty_state")}
                        </div>
                      )}
                      {sortedPortfolio.map((item, idx) => (
                        <PerfRowComponent
                          key={`perf-desk-${item.id}`}
                          item={item}
                          isTopPick={topPickInfo?.ticker === item.ticker}
                          liveQuotes={liveQuotes}
                          onSetSelectedTicker={(t: string) =>
                            handleAnalyzeSingle(t, false, true)
                          }
                        />
                      ))}
                      {portfolio.length > 0 && !loading && (
                        <PortfolioPerfRowComponent
                          portfolio={portfolio}
                          liveQuotes={liveQuotes}
                          portfolioType={portfolioType}
                        />
                      )}
                      {portfolio.length > 0 && !loading && (
                        <div className="px-6 py-4">
                          <button
                            onClick={() => setIsAddTickerModalOpen(true)}
                            className="w-full py-2.5 border border-dashed border-zinc-700/50 hover:border-zinc-500 rounded-sm text-[10px] uppercase font-bold tracking-widest text-zinc-300 hover:text-emerald-400 hover:bg-zinc-800/30 transition-all flex items-center justify-center gap-2"
                          >
                            <Plus size={12} /> {t("portfolio.add_ticker")}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <table className="w-full text-left table-fixed">
                      <thead>
                        <tr className="bg-zinc-900/80 border-b border-zinc-800 text-[10px] font-black text-zinc-400 uppercase tracking-widest">
                          <th className="px-2 py-4 w-8"></th>
                          <th
                            className="px-4 py-4 w-36 cursor-pointer hover:text-zinc-300 transition-colors group select-none whitespace-nowrap"
                            onClick={() => handleSort("ticker")}
                          >
                            <span className="flex items-center gap-1">
                              {t("common.ticker")}
                              {sortColumn === "ticker" ? (
                                sortDirection === "desc" ? (
                                  <span className="text-[10px] text-emerald-500">
                                    Z-A
                                  </span>
                                ) : (
                                  <span className="text-[10px] text-emerald-500">
                                    A-Z
                                  </span>
                                )
                              ) : (
                                <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                              )}
                            </span>
                          </th>
                          <th className="px-4 py-4 text-right w-28 whitespace-nowrap">
                            {t("analysis.price_observation", {
                              defaultValue: "Price",
                            })}{" "}
                            / PERF
                          </th>
                          {portfolioType !== "WATCHLIST" && (
                            <th
                              className="px-2 py-4 text-right w-20 cursor-pointer hover:text-zinc-300 transition-colors group select-none whitespace-nowrap"
                              onClick={() => handleSort("allocation")}
                            >
                              <span className="flex items-center justify-end gap-1 whitespace-nowrap">
                                {t("common.alloc")} (%){" "}
                                <Edit2 size={10} className="inline opacity-50" />
                                {sortColumn === "allocation" ? (
                                  sortDirection === "desc" ? (
                                    <ArrowDown className="w-3 h-3 text-emerald-500" />
                                  ) : (
                                    <ArrowUp className="w-3 h-3 text-emerald-500" />
                                  )
                                ) : (
                                  <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                                )}
                              </span>
                            </th>
                          )}
                          {!showTechFundDetails ? (
                            <th
                              className="px-4 py-4 text-center cursor-pointer hover:text-zinc-300 transition-colors group select-none relative whitespace-nowrap"
                              onClick={() => handleSort("overall")}
                            >
                              <span className="flex items-center justify-center gap-1 whitespace-nowrap">
                                {t("analysis.overall_recommendation")}
                                {sortColumn === "overall" ? (
                                  sortDirection === "desc" ? (
                                    <ArrowDown className="w-3 h-3 text-emerald-500" />
                                  ) : (
                                    <ArrowUp className="w-3 h-3 text-emerald-500" />
                                  )
                                ) : (
                                  <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                                )}
                              </span>
                            </th>
                          ) : (
                            <>
                              <th
                                className="px-4 py-4 text-center cursor-pointer hover:text-zinc-300 transition-colors group select-none relative whitespace-nowrap"
                                onClick={() => handleSort("technical")}
                              >
                                <span className="flex items-center justify-center gap-1 whitespace-nowrap">
                                  {t("common.technical")}
                                  {sortColumn === "technical" ? (
                                    sortDirection === "desc" ? (
                                      <ArrowDown className="w-3 h-3 text-emerald-500" />
                                    ) : (
                                      <ArrowUp className="w-3 h-3 text-emerald-500" />
                                    )
                                  ) : (
                                    <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                                  )}
                                </span>
                              </th>
                              <th
                                className="pl-5 pr-4 py-4 text-center cursor-pointer hover:text-zinc-300 transition-colors group select-none relative whitespace-nowrap"
                                onClick={() => handleSort("fundamental")}
                              >
                                <span className="flex items-center justify-center gap-1 whitespace-nowrap">
                                  {t("common.fundamental")}
                                  {sortColumn === "fundamental" ? (
                                    sortDirection === "desc" ? (
                                      <ArrowDown className="w-3 h-3 text-emerald-500" />
                                    ) : (
                                      <ArrowUp className="w-3 h-3 text-emerald-500" />
                                    )
                                  ) : (
                                    <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                                  )}
                                </span>
                              </th>
                            </>
                          )}
                          <th className="px-4 py-4 pl-6 text-center w-36 whitespace-nowrap">
                            {t("common.analyze")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-800/40">
                        {viewMode === "LIST" ? (
                          <SortableContext
                            items={sortedPortfolio.map((i) => `desk-${i.id}`)}
                            strategy={verticalListSortingStrategy}
                          >
                            {sortedPortfolio.map((item, idx) => (
                              <DesktopRowComponent
                                key={`desktop-${item.id}`}
                                sortableId={`desk-${item.id}`}
                                item={item}
                                isSelected={selectedTicker === item.ticker}
                                isTopPick={topPickInfo?.ticker === item.ticker}
                                onUpdateTicker={handleUpdateTicker}
                                onUpdateItem={handleUpdateItem}
                                onSetSelectedTicker={(t: string) =>
                                  handleAnalyzeSingle(t, false, true)
                                }
                                onRemoveItem={handleRemoveItem}
                                TickerEditorComponent={TickerEditor}
                                liveQuotes={liveQuotes}
                                listPerfMetric={listPerfMetric}
                                setListPerfMetric={setListPerfMetric}
                                showTechFundDetails={showTechFundDetails}
                                portfolioType={portfolioType}
                                tradeDuration={tradeDuration}
                              />
                            ))}
                          </SortableContext>
                        ) : (
                          <SortableContext
                            items={orderedThemes.map((t) => `category-${t}`)}
                            strategy={verticalListSortingStrategy}
                          >
                            {(() => {
                              const flatDeskThemeList = orderedThemes.flatMap(
                                (t) => groupedPortfolio[t] || [],
                              );
                              return (
                                <SortableContext
                                  items={flatDeskThemeList.map((i) => `desk-${i.id}`)}
                                  strategy={verticalListSortingStrategy}
                                >
                                  {orderedThemes.map((themeName) => {
                                    const items = groupedPortfolio[themeName] || [];
                                    const themeObjArr = items.map(
                                      (p) =>
                                        parseFloat(
                                          p.allocation.replace(/[^0-9.-]+/g, ""),
                                        ) || 0,
                                    );
                                    const themeSubtotal = themeObjArr.reduce(
                                      (a, b) => a + b,
                                      0,
                                    );
                                    return (
                                      <React.Fragment key={`theme-${themeName}`}>
                                        <tr className="bg-zinc-800/80 border-y border-zinc-700/50">
                                          <td colSpan={(showTechFundDetails ? 7 : 6) - (portfolioType === "WATCHLIST" ? 1 : 0)} className="p-0">
                                            <SortableCategoryHeader
                                              id={`category-${themeName}`}
                                              className="px-6 py-2"
                                            >
                                              <div className="flex items-center justify-between w-full min-w-0 gap-2">
                                                <ThemeInput
                                                  themeName={themeName}
                                                  onChange={handleUpdateTheme}
                                                  className="bg-transparent text-emerald-400 font-bold uppercase tracking-widest text-[11px] outline-none border-b border-transparent focus:border-zinc-500 flex-1 min-w-0 w-full"
                                                />
                                                <div className="flex items-center gap-2 shrink-0">
                                                  {portfolioType !== "WATCHLIST" && (
                                                    <span className="text-xs font-mono font-bold text-emerald-400 bg-emerald-950/40 border border-emerald-500/30 px-2.5 py-0.5 rounded-md shadow-sm">
                                                      {themeSubtotal.toFixed(
                                                        hasDecimals ? 2 : 0,
                                                      )}%
                                                    </span>
                                                  )}
                                                  {items.length === 0 && (
                                                    <button
                                                      onClick={() =>
                                                        handleDeleteCategory(themeName)
                                                      }
                                                      className="text-zinc-300 hover:text-red-400 transition-colors"
                                                      title={
                                                        t("common.delete_category") || "Delete"
                                                      }
                                                    >
                                                      <Trash2 size={12} />
                                                    </button>
                                                  )}
                                                </div>
                                              </div>
                                            </SortableCategoryHeader>
                                          </td>
                                        </tr>
                                        {items.map((item, idx) => (
                                          <React.Fragment key={item.ticker}>
                                            <DesktopRowComponent
                                              key={`desktop-theme-${item.id}`}
                                              sortableId={`desk-${item.id}`}
                                              item={item}
                                              isSelected={selectedTicker === item.ticker}
                                              isTopPick={topPickInfo?.ticker === item.ticker}
                                              onUpdateTicker={handleUpdateTicker}
                                              onUpdateItem={handleUpdateItem}
                                              onSetSelectedTicker={(t: string) =>
                                                handleAnalyzeSingle(t, false, true)
                                              }
                                              onRemoveItem={handleRemoveItem}
                                              TickerEditorComponent={TickerEditor}
                                              liveQuotes={liveQuotes}
                                              listPerfMetric={listPerfMetric}
                                              setListPerfMetric={setListPerfMetric}
                                              showTechFundDetails={showTechFundDetails}
                                              portfolioType={portfolioType}
                                              tradeDuration={tradeDuration}
                                            />
                                          </React.Fragment>
                                        ))}
                                      </React.Fragment>
                                    );
                                  })}
                                </SortableContext>
                              );
                            })()}
                          </SortableContext>
                        )}
                        {portfolio.length === 0 && !loading && (
                          <tr>
                            <td
                              colSpan={showTechFundDetails ? 7 : 6}
                              className="px-6 py-16 text-center text-zinc-300 text-sm"
                            >
                              {t("portfolio.empty_state")}
                            </td>
                          </tr>
                        )}
                        {portfolio.length > 0 && !loading && (
                          <tr className="hover:bg-transparent">
                            <td colSpan={(showTechFundDetails ? 7 : 6) - (portfolioType === "WATCHLIST" ? 1 : 0)} className="px-6 py-4">
                              <button
                                onClick={() => setIsAddTickerModalOpen(true)}
                                className="w-full py-2.5 border border-dashed border-zinc-700/50 hover:border-zinc-500 rounded-sm text-[10px] uppercase font-bold tracking-widest text-zinc-300 hover:text-emerald-400 hover:bg-zinc-800/30 transition-all flex items-center justify-center gap-2"
                              >
                                <Plus size={12} /> {t("portfolio.add_ticker")}
                              </button>
                            </td>
                          </tr>
                        )}
                      </tbody>
                      {portfolio.length > 0 && !loading && portfolioType !== "WATCHLIST" && (
                        <tfoot className="bg-zinc-900/50 border-t border-zinc-800 text-[10px] uppercase font-bold tracking-widest text-zinc-300">
                          <tr>
                            <td colSpan={3} className="px-6 py-3.5 text-right">
                              <span className="text-[10px] uppercase font-bold tracking-widest text-zinc-300">
                                {t("portfolio.total_allocation")}
                              </span>
                            </td>
                            <td className="px-6 py-3.5 text-right font-mono text-sm font-bold text-emerald-400">
                              {totalAllocation.toFixed(hasDecimals ? 2 : 0)}%
                            </td>
                            <td colSpan={showTechFundDetails ? 3 : 2}></td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  )}
                </div>

                {/* Dedicated Risk & Trade Sizing Parameters Bar */}
                {handleUpdatePortfolioRiskSettings && portfolio.length > 0 && !loading && portfolioType !== "WATCHLIST" && (
                  <div className="border-t border-zinc-800 bg-zinc-950/80 px-3 sm:px-5 py-2.5">
                    <div className="flex flex-wrap items-center justify-between gap-y-2 gap-x-4">
                      <div className="flex items-center gap-1.5 shrink-0">
                        <div className="p-1 rounded bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 shrink-0">
                          <Shield size={12} />
                        </div>
                        <TooltipSpan
                          text={t("analysis.risk_parameters", { defaultValue: "Risk Parameters" })}
                          tooltip={t("analysis.risk_parameters_tooltip", { defaultValue: "Default risk tolerance and single-stock exposure limits used by the engine to calculate trade sizing recommendations for this portfolio." })}
                          placement="top"
                          positionClassName="left-0 sm:left-auto"
                          textClassName="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-zinc-300 font-sans"
                        />
                      </div>

                      <div className="flex flex-wrap items-center gap-2.5 sm:gap-4 font-mono text-xs">
                        <div className="flex items-center gap-1.5 bg-zinc-900/90 border border-zinc-800/80 rounded px-2 py-1 shadow-sm">
                          <label
                            id="max-risk-per-trade-label"
                            htmlFor="max-risk-per-trade-select"
                            className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 flex items-center whitespace-nowrap cursor-pointer"
                          >
                            <TooltipSpan
                              text={t("analysis.max_risk_per_trade", { defaultValue: "Max Risk" })}
                              tooltip={t("analysis.max_risk_per_trade_tooltip", { defaultValue: "The maximum acceptable portfolio loss percentage you are willing to risk on a single trade if the stop-loss is triggered." })}
                              placement="top"
                              positionClassName="left-0 sm:left-1/2 sm:-translate-x-1/2"
                              textClassName="text-zinc-400 hover:text-zinc-300 cursor-help"
                              isFocusable={false}
                            />:
                          </label>
                          <select
                            id="max-risk-per-trade-select"
                            name="max-risk-per-trade"
                            aria-labelledby="max-risk-per-trade-label"
                            aria-label={t("analysis.max_risk_per_trade", { defaultValue: "Max Risk Per Trade" })}
                            value={maxRiskPerTradePct}
                            onChange={(e) => handleUpdatePortfolioRiskSettings(parseFloat(e.target.value), maxAllocationCapPct, tradeDuration)}
                            className="bg-zinc-950 border border-zinc-700/80 hover:border-zinc-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-emerald-400 rounded px-1.5 py-0.5 text-xs font-mono font-bold transition-colors cursor-pointer outline-none"
                          >
                            <option value={0.005}>0.5%</option>
                            <option value={0.01}>1.0%</option>
                            <option value={0.015}>1.5%</option>
                            <option value={0.02}>2.0%</option>
                            <option value={0.025}>2.5%</option>
                            <option value={0.03}>3.0%</option>
                            <option value={0.05}>5.0%</option>
                          </select>
                        </div>

                        <div className="flex items-center gap-1.5 bg-zinc-900/90 border border-zinc-800/80 rounded px-2 py-1 shadow-sm">
                          <label
                            id="max-alloc-cap-label"
                            htmlFor="max-alloc-cap-select"
                            className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 flex items-center whitespace-nowrap cursor-pointer"
                          >
                            <TooltipSpan
                              text={t("analysis.max_allocation_cap", { defaultValue: "Max Alloc" })}
                              tooltip={t("analysis.max_allocation_cap_tooltip", { defaultValue: "The maximum portfolio concentration percentage permitted in a single stock." })}
                              placement="top"
                              positionClassName="right-0 sm:left-1/2 sm:-translate-x-1/2"
                              textClassName="text-zinc-400 hover:text-zinc-300 cursor-help"
                              isFocusable={false}
                            />:
                          </label>
                          <select
                            id="max-alloc-cap-select"
                            name="max-allocation-cap"
                            aria-labelledby="max-alloc-cap-label"
                            aria-label={t("analysis.max_allocation_cap", { defaultValue: "Max Allocation Cap" })}
                            value={maxAllocationCapPct}
                            onChange={(e) => handleUpdatePortfolioRiskSettings(maxRiskPerTradePct, parseFloat(e.target.value), tradeDuration)}
                            className="bg-zinc-950 border border-zinc-700/80 hover:border-zinc-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-emerald-400 rounded px-1.5 py-0.5 text-xs font-mono font-bold transition-colors cursor-pointer outline-none"
                          >
                            <option value={0.05}>5%</option>
                            <option value={0.10}>10%</option>
                            <option value={0.15}>15%</option>
                            <option value={0.20}>20%</option>
                            <option value={0.25}>25%</option>
                            <option value={0.30}>30%</option>
                            <option value={1.0}>100%</option>
                          </select>
                        </div>
                        
                        <div className="flex items-center gap-1.5 bg-zinc-900/90 border border-zinc-800/80 rounded px-2 py-1 shadow-sm">
                          <label
                            id="trade-duration-label"
                            htmlFor="trade-duration-select"
                            className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 flex items-center whitespace-nowrap cursor-pointer"
                          >
                            <TooltipSpan
                              text={t("analysis.horizon", { defaultValue: "Horizon" })}
                              tooltip={t("analysis.trade_duration_tooltip", { defaultValue: "Trade duration dynamically shifts confluence weight between Technicals (short) and Fundamentals (long)." })}
                              placement="top"
                              positionClassName="right-0 sm:left-1/2 sm:-translate-x-1/2"
                              textClassName="text-zinc-400 hover:text-zinc-300 cursor-help"
                              isFocusable={false}
                            />:
                          </label>
                          <select
                            id="trade-duration-select"
                            name="trade-duration"
                            aria-labelledby="trade-duration-label"
                            aria-label={t("analysis.trade_duration", { defaultValue: "Trade Duration" })}
                            value={tradeDuration}
                            onChange={(e) => handleUpdatePortfolioRiskSettings(maxRiskPerTradePct, maxAllocationCapPct, e.target.value)}
                            className="bg-zinc-950 border border-zinc-700/80 hover:border-zinc-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-emerald-400 rounded px-1.5 py-0.5 text-xs font-mono font-bold transition-colors cursor-pointer outline-none"
                          >
                            <option value="short_swing">{t("analysis.trade_duration_short_swing", { defaultValue: "Swing (Short)" })}</option>
                            <option value="position">{t("analysis.trade_duration_position", { defaultValue: "Position (Med)" })}</option>
                            <option value="long_term">{t("analysis.trade_duration_long_term", { defaultValue: "Hold (Long)" })}</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </DndContext>
            <p className="text-[10px] text-zinc-400 italic flex items-center gap-2">
              <Activity size={10} />
              {t("analysis.unified_hub")} •{" "}
              {t("analysis.data_refreshed", {
                time: new Date().toLocaleTimeString(),
              })}
            </p>
          </section>

      <AddTickerModal
        isOpen={isAddTickerModalOpen}
        portfolioType={portfolioType}
        onConfirm={(symbol, allocation) => {
          handleAddRow(symbol, allocation);
          setIsAddTickerModalOpen(false);
        }}
        onCancel={() => setIsAddTickerModalOpen(false)}
      />
    </>
  );
}
